import { useEffect, useRef } from 'react';
import { FormikProps } from 'formik';
import { RadioInput } from 'components/core';
import AssignableGroupPicker from 'containers/assignable-group-picker';
import AssignableUserPicker from 'containers/assignable-user-picker';

interface AssigneeSelectorProps<TFormValues extends { assigneeType: 'group' | 'user'; assigneeId: number; assigneeName: string }> {
  form: FormikProps<TFormValues>;
  groupHelpText?: string;
  userHelpText?: string;
}

const AssigneeSelector = <TFormValues extends { assigneeType: 'group' | 'user'; assigneeId: number; assigneeName: string }>({
  form,
  groupHelpText,
  userHelpText,
}: AssigneeSelectorProps<TFormValues>) => {
  const prevAssigneeTypeRef = useRef(form.values.assigneeType);

  useEffect(() => {
    if (prevAssigneeTypeRef.current !== form.values.assigneeType) {
      form.setFieldValue('assigneeId', 0);
      form.setFieldValue('assigneeName', '');
      prevAssigneeTypeRef.current = form.values.assigneeType;
    }
  }, [form.values.assigneeType, form]);

  const assigneeTypeOptions = [
    { value: 'group', label: 'Assign to a Group', description: 'Assign to a team (e.g., IT Team, Compliance)' },
    { value: 'user', label: 'Assign to a User', description: 'Assign to a specific person' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <RadioInput id="assigneeType" label="Assignee Type" options={assigneeTypeOptions} required={true} {...form} />
      </div>

      {form.values.assigneeType === 'group' && (
        <div>
          <AssignableGroupPicker id="assigneeId" label="Select Group" required {...form} />
          <p className="mt-2 text-sm text-gray-500">
            {groupHelpText || 'All members of the selected group will receive a notification about this request.'}
          </p>
        </div>
      )}

      {form.values.assigneeType === 'user' && (
        <div>
          <AssignableUserPicker id="assigneeId" label="Select User" required {...form} />
          <p className="mt-2 text-sm text-gray-500">{userHelpText || 'The selected user will receive a notification about this request.'}</p>
        </div>
      )}
    </div>
  );
};

export default AssigneeSelector;
